import React from "react";
import { Link } from "react-router-dom";
import { Youtube, ExternalLink, Mail, Phone, MapPin } from "lucide-react";

const COAT_OF_ARMS = "https://customer-assets.emergentagent.com/job_92e13f48-99c4-452a-b014-1be6a3b8b28b/artifacts/e3352bxk_Mock%20up%20Jonesaica%20Yah%E2%80%99shar%E2%80%99al.png";

const socialLinks = [
  {
    name: "YouTube (Shaba Sharah)",
    url: "https://youtube.com/@shabasharah?si=94NEL8uvqAqjsCg4",
    icon: Youtube,
  },
  {
    name: "TikTok (Yunah'Shach'Qal Yashar'al)",
    url: "https://www.tiktok.com/@jonesaicayahsharal7?is_from_webapp=1&sender_device=pc",
    icon: () => (
      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
      </svg>
    ),
  },
  {
    name: "The Qadash Sheep (TikTok)",
    url: "https://www.tiktok.com/@theqadashsheep77",
    icon: () => (
      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
      </svg>
    ),
  },
  {
    name: "The Woman In Gold (YouTube)",
    url: "https://www.youtube.com/@TheWomanInGold77",
    icon: Youtube,
  },
];

const quickLinks = [
  { label: "Home", href: "/" },
  { label: "About the Nation", href: "/about" },
  { label: "Governance", href: "/governance" },
  { label: "Become YaSHaRUN", href: "/become-national" },
  { label: "Programs", href: "/programs" },
  { label: "Fight Fi Tash", href: "/fightfitash" },
];

const resourceLinks = [
  { label: "Media Hub", href: "/media" },
  { label: "News & Blog", href: "/news" },
  { label: "Shop / Merch", href: "/shop" },
  { label: "Donate / Support", href: "/donate" },
  { label: "Contact Us", href: "/contact" },
  { label: "Shaba Sharah", href: "/brands/shaba" },
  { label: "Members Portal", href: "/members" },
  { label: "Yasharal Live Login", href: "/yasharal-live/login" },
  { label: "Privacy Policy", href: "/privacy" },
  { label: "Terms of Service", href: "/terms" },
];

export default function Footer() {
  return (
    <footer className="bg-royal-blue text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <img src={COAT_OF_ARMS} alt="Coat of Arms" className="h-16 w-auto" />
            </Link>
            <h3 className="font-heading text-xl font-bold mb-2">The Kingdom of Yunah'Shach'Qal Yashar'al</h3>
            <p className="text-slate-400 text-sm mb-4">A sovereign micro-nation rooted in ecclesiastic law, self-governance, and spiritual awakening.</p>
            <p className="text-kingdom-gold text-sm font-semibold italic">"Seek The Ancient Path"</p>
          </div>

          <div>
            <h4 className="font-heading text-lg font-semibold mb-4 text-kingdom-gold">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.href}><Link to={link.href} className="text-slate-300 hover:text-white transition-colors text-sm">{link.label}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-lg font-semibold mb-4 text-kingdom-gold">Resources</h4>
            <ul className="space-y-2">
              {resourceLinks.map((link) => (
                <li key={link.href}><Link to={link.href} className="text-slate-300 hover:text-white transition-colors text-sm">{link.label}</Link></li>
              ))}
              <li>
                <a href="https://solutions.yahsharal.info/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-slate-300 hover:text-white transition-colors text-sm">
                  Infrastructure / Solar <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-lg font-semibold mb-4 text-kingdom-gold">Contact Us</h4>
            <ul className="space-y-3 mb-6">
              <li className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-kingdom-gold" /><a href="mailto:admin@yasharal.vip" className="text-slate-300 hover:text-white">admin@yasharal.vip</a></li>
              <li className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-kingdom-gold" /><span className="text-slate-300">(224) CALL-YAH (225-5924)</span></li>
              <li className="flex items-start gap-2 text-sm"><MapPin className="w-4 h-4 text-kingdom-gold mt-0.5" /><span className="text-slate-300">777 Qadash Lane, Yunah'Shach'Qal, Yashar'al</span></li>
            </ul>

            <h4 className="font-heading text-lg font-semibold mb-3 text-kingdom-gold">Follow Us</h4>
            <div className="flex flex-wrap gap-3">
              {socialLinks.map((social) => (
                <a key={social.name} href={social.url} target="_blank" rel="noopener noreferrer" className="p-2 bg-slate-800 rounded hover:bg-kingdom-gold transition-colors" title={social.name}>
                  {typeof social.icon === "function" ? <social.icon /> : <social.icon className="w-5 h-5" />}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-400">
            <p>© {new Date().getFullYear()} The Kingdom of Yunah'Shach'Qal Yashar'al. All rights reserved.</p>
            <p className="text-center">"Free by blood. Protected by YaHUaH. No contract, no consent, no court fi Babylon."</p>
            <div className="flex gap-4">
              <Link to="/privacy" className="hover:text-white">Privacy</Link>
              <Link to="/terms" className="hover:text-white">Terms</Link>
              <a href="https://www.etsy.com/shop/AncientPathFactory" target="_blank" rel="noopener noreferrer" className="hover:text-white flex items-center gap-1">Shop on Etsy <ExternalLink className="w-3 h-3" /></a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
